import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme,
} from '@mui/material';
import {
    ENCONTRADO,
    FALHOU,
    consultarCadastro,
    limparIdentificacao,
} from '../../services/consultaDeCadastro';

/**
 * O cadastro encontrado, mostrado ANTES de associar.
 *
 * Ponto 15 da Gisele (20/10/2026): "ao associar o CNPJ ou registro do cliente
 * no WhatsApp, mostrar os dados encontrados na própria janela de edição".
 *
 * ## O que mudou para quem usa
 *
 * Antes, digitar o registro em "Associar cliente" e apertar Enter associava na
 * hora. Um dígito trocado ligava a conversa à empresa errada, e o operador só
 * descobria depois -- pelo nome errado na ficha, ou nunca.
 *
 * Agora o Enter CONSULTA. O vínculo só acontece no botão desta janela, com a
 * razão social, o CNPJ e a cidade na frente de quem decide.
 *
 * ## O aviso de cadastro inativo
 *
 * `ativo` tem três valores, e os três querem dizer coisas diferentes:
 * verdadeiro (cadastro ativo), falso (INATIVO -- avisa) e nulo (a fonte não
 * classifica, como a base importada). Tratar nulo como inativo faria o
 * operador recusar um cliente legítimo; tratar como ativo esconderia o aviso
 * de quem precisa dele. Por isso o aviso só aparece no falso.
 *
 * Avisar, e não bloquear: há cliente inativo que liga, e quem decide se
 * atende é o operador.
 */
const ConsultaDeCadastro = ({ aberto, identificacao, onAssociar, onFechar }) => {
    const theme = useTheme();
    const [consultando, setConsultando] = useState(false);
    const [cadastro, setCadastro] = useState(null);
    const [erro, setErro] = useState(null);
    const [falhouAConsulta, setFalhouAConsulta] = useState(false);

    const consultar = useCallback(async (chave) => {
        setConsultando(true);
        setCadastro(null);
        setErro(null);
        setFalhouAConsulta(false);

        const r = await consultarCadastro(chave);

        if (r.situacao === ENCONTRADO) {
            setCadastro(r.cadastro);
        } else {
            setErro(r.mensagem);
            // Falha de consulta oferece "Tentar de novo"; "nao encontrado" nao
            // -- insistir na mesma identificacao daria a mesma resposta, e o
            // botao sugeriria o contrario.
            setFalhouAConsulta(r.situacao === FALHOU);
        }

        setConsultando(false);
    }, []);

    useEffect(() => {
        if (!aberto) return;

        consultar(identificacao);
    }, [aberto, identificacao, consultar]);

    const limpa = limparIdentificacao(identificacao);

    return (
        <Dialog open={Boolean(aberto)} onClose={onFechar} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ color: `${theme.palette.text.primary} !important` }}>
                Cadastro encontrado
            </DialogTitle>
            <DialogContent dividers>
                {consultando && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }} data-testid="consultando">
                        <CircularProgress size={18} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Consultando {limpa}...
                        </Typography>
                    </Box>
                )}

                {!consultando && erro && (
                    <Alert severity={falhouAConsulta ? 'warning' : 'info'} data-testid="erro-da-consulta">
                        {erro}
                    </Alert>
                )}

                {!consultando && cadastro && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cadastro-encontrado">
                        {/*
                          * `ativo === false` e nao `!ativo`: nulo significa "a
                          * fonte nao classifica", e mostrar INATIVO ali faria o
                          * operador recusar um cliente legitimo.
                          */}
                        {cadastro.ativo === false && (
                            <Alert severity="warning" data-testid="aviso-de-inativo">
                                Este cadastro está INATIVO na base. Confira antes de associar.
                            </Alert>
                        )}

                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                            {cadastro.razao_social || cadastro.nome || 'Sem razão social'}
                        </Typography>

                        <LinhaDoCadastro rotulo="Registro" valor={cadastro.codigo} theme={theme} />
                        <LinhaDoCadastro rotulo="CNPJ" valor={cadastro.cnpj} theme={theme} />
                        <LinhaDoCadastro
                            rotulo="Cidade"
                            valor={[cadastro.cidade, cadastro.estado].filter(Boolean).join(' - ')}
                            theme={theme}
                        />
                        <LinhaDoCadastro rotulo="Telefone" valor={cadastro.telefone} theme={theme} />
                        <LinhaDoCadastro rotulo="E-mail" valor={cadastro.email} theme={theme} />
                        <LinhaDoCadastro rotulo="Departamento" valor={cadastro.grupo_atendimento} theme={theme} />
                        <LinhaDoCadastro rotulo="Produto" valor={cadastro.produto} theme={theme} />
                        <LinhaDoCadastro rotulo="Versão" valor={cadastro.versao} theme={theme} />

                        {cadastro.ativo === true && (
                            <Box sx={{ mt: 0.5 }}>
                                <Chip label="Cadastro ativo" size="small" color="success" variant="outlined" />
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onFechar} sx={{ color: `${theme.palette.text.secondary} !important`, mr: 'auto' }}>
                    Cancelar
                </Button>
                {falhouAConsulta && (
                    <Button onClick={() => consultar(identificacao)} data-testid="tentar-de-novo">
                        Tentar de novo
                    </Button>
                )}
                {/*
                  * O botao de associar NAO existe sem cadastro na tela. Deixa-lo
                  * ali desabilitado convidaria a clicar justamente quando o
                  * sistema nao sabe o que vai associar -- que e o caso que esta
                  * janela existe para evitar.
                  */}
                {cadastro && (
                    <Button
                        variant="contained"
                        onClick={() => onAssociar(limpa, cadastro)}
                        data-testid="associar-cadastro"
                    >
                        Associar a esta empresa
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

/** Uma linha do cadastro. Campo vazio não vira linha em branco. */
const LinhaDoCadastro = ({ rotulo, valor, theme }) => {
    if (!valor) return null;

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: 92, flexShrink: 0 }}>
                {rotulo}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary, wordBreak: 'break-word' }}>
                {valor}
            </Typography>
        </Box>
    );
};

export default ConsultaDeCadastro;
